interface DataTypeMap {
  [key: string]: string;
}

/**
 * This is what is return from gql for `dataType`
 */
export const CLIENT_DATA_TYPES: DataTypeMap = {
  COMPLAINT: 'Complaint',
  CUSTOM_EVENT: 'CustomEvent',
  EMAIL_CLICK: 'Click',
  EMAIL_CLOSE: 'Close',
  EMAIL_DELETE: 'Delete',
  EMAIL_DELIVERY: 'Delivery',
  EMAIL_OPEN: 'Open',
  EMAIL_SEND_SKIP: 'SendSkip',
  EMAIL_SEND: 'Send',
  EMAIL_SUBSCRIBE: 'Subscribe',
  EMAIL_UNSUBSCRIBE: 'UnSubscribe',
  HOSTED_UNSUBSCRIBE_CLICK: 'HostedUnsubscribeClick',
  IN_APP_CLICK: 'InAppClick',
  IN_APP_CLOSE: 'InAppClose',
  IN_APP_DELETE: 'InAppDelete',
  IN_APP_DELIVERY: 'InAppDelivery',
  IN_APP_OPEN: 'InAppOpen',
  IN_APP_SEND_SKIP: 'InAppSendSkip',
  IN_APP_SEND: 'InAppSend',
  INBOX_MESSAGE_IMPRESSION: 'InboxMessageImpression',
  INBOX_SESSION: 'InboxSession',
  PURCHASE: 'Purchase',
  PUSH_BOUNCE: 'PushBounce',
  PUSH_OPEN: 'PushOpen',
  PUSH_SEND_SKIP: 'PushSendSkip',
  PUSH_SEND: 'PushSend',
  PUSH_UNINSTALL: 'PushUninstall',
  SEND_SKIP: 'SendSkip',
  SMS_BOUNCE: 'SMSBounce',
  SMS_RECEIVED: 'SMSReceived',
  SMS_CLICK: 'SMSClick',
  SMS_SEND_SKIP: 'SMSSendSkip',
  SMS_SEND: 'SMSSend',
  USER: 'User',
  WEB_PUSH_CLICK: 'WebPushClick',
  WEB_PUSH_SEND_SKIP: 'WebPushSendSkip',
  WEB_PUSH_SEND: 'WebPushSend',
} as const;

/**
 * This corresponds to `eventType` on the backend, but is often given to
 * frontend as `dataType`
 */
export const EVENT_TYPES: DataTypeMap = {
  COMPLAINT: 'complaint',
  CUSTOM_EVENT: 'customEvent',
  EMAIL_CLICK: 'emailClick',
  EMAIL_CLOSE: 'emailClose',
  EMAIL_DELETE: 'emailDelete',
  EMAIL_DELIVERY: 'emailDelivery',
  EMAIL_OPEN: 'emailOpen',
  EMAIL_SEND_SKIP: 'emailSendSkip',
  EMAIL_SEND: 'emailSend',
  EMAIL_SUBSCRIBE: 'emailSubscribe',
  EMAIL_UNSUBSCRIBE: 'emailUnSubscribe',
  HOSTED_UNSUBSCRIBE_CLICK: 'hostedUnsubscribeClick',
  IN_APP_CLICK: 'inAppClick',
  IN_APP_CLOSE: 'inAppClose',
  IN_APP_DELETE: 'inAppDelete',
  IN_APP_DELIVERY: 'inAppDelivery',
  IN_APP_OPEN: 'inAppOpen',
  IN_APP_SEND_SKIP: 'inAppSendSkip',
  IN_APP_SEND: 'inAppSend',
  INBOX_MESSAGE_IMPRESSION: 'inboxMessageImpression',
  INBOX_SESSION: 'inboxSession',
  PURCHASE: 'purchase',
  PUSH_BOUNCE: 'pushBounce',
  PUSH_OPEN: 'pushOpen',
  PUSH_SEND_SKIP: 'pushSendSkip',
  PUSH_SEND: 'pushSend',
  PUSH_UNINSTALL: 'pushUninstall',
  SEND_SKIP: 'sendSkip',
  SMS_BOUNCE: 'smsBounce',
  SMS_RECEIVED: 'smsReceived',
  SMS_CLICK: 'smsClick',
  SMS_SEND_SKIP: 'smsSendSkip',
  SMS_SEND: 'smsSend',
  USER: 'user',
  WEB_PUSH_CLICK: 'webPushClick',
  WEB_PUSH_SEND_SKIP: 'webPushSendSkip',
  WEB_PUSH_SEND: 'webPushSend',
} as const;

export type ClientDataType = (typeof CLIENT_DATA_TYPES)[keyof typeof CLIENT_DATA_TYPES];
export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

type EventToDataTypeMap = {
  [key in EventType]?: ClientDataType;
};

type DataToEventTypeMap = {
  [key in ClientDataType]?: EventType;
};

const EVENT_TYPE_TO_CLIENT_DATA_TYPE_MAP: EventToDataTypeMap = Object.keys(
  CLIENT_DATA_TYPES,
).reduce(
  (acc, key) => ({
    ...acc,
    [EVENT_TYPES[key]]: CLIENT_DATA_TYPES[key],
  }),
  {},
);

const CLIENT_DATA_TYPE_TO_EVENT_TYPE_MAP: DataToEventTypeMap = Object.keys(
  CLIENT_DATA_TYPES,
).reduce(
  (acc, key) => ({
    ...acc,
    [CLIENT_DATA_TYPES[key]]: EVENT_TYPES[key],
  }),
  {},
);

export const DATA_TYPE_MAP = {
  ...CLIENT_DATA_TYPE_TO_EVENT_TYPE_MAP,
  ...EVENT_TYPE_TO_CLIENT_DATA_TYPE_MAP,
};

export const getEventTypeFromClientDataType = (eventType: EventType): ClientDataType | void =>
  EVENT_TYPE_TO_CLIENT_DATA_TYPE_MAP[eventType];

export const getClientDataTypeFromEventType = (dataType: ClientDataType): EventType | void =>
  CLIENT_DATA_TYPE_TO_EVENT_TYPE_MAP[dataType];
